import { VideoContainerUi } from 'common/components/entities/Video/ui/VideoContainerUi'
import VideoUi from 'common/components/entities/Video/ui/VideoUi'
import React, { useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player/lazy'
import { BorderEntityInterface } from 'src/shared/schemas/border.schema'
import { isElementVisibleInViewport } from 'src/shared/utils/is-element-visible-in-viewport'
import UnMute from './ui/UnMuteUi'

type BasePlayerProps = BorderEntityInterface & {
  attrId: string
  url: string
  autoplay: boolean
  playing?: boolean
  setPlaying?: React.Dispatch<React.SetStateAction<boolean>>
  controls?: boolean
  boxShadow?: string
  mobileBoxShadow?: string
  onReady?: (video: ReactPlayer) => void
  posterUrl?: string
}

function BasePlayer({
  attrId,
  url,
  border,
  mobileBorder,
  controls = false,
  autoplay = false,
  playing = false,
  setPlaying,
  boxShadow,
  mobileBoxShadow,
  onReady,
  posterUrl,
}: BasePlayerProps) {
  const [muted, setMuted] = useState(false)
  const videoRef = useRef<ReactPlayer>(null)
  const [shouldBeLoaded, setShouldBeLoaded] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = containerRef.current

    if (node && !shouldBeLoaded) {
      // NOTE: Fallback for browsers/environments where IO is unreliable/unavailable.
      if (typeof IntersectionObserver === 'undefined') {
        setShouldBeLoaded(true)
        // NOTE: If the video is already on screen on first paint, don't wait for IO.
      } else if (isElementVisibleInViewport(node)) {
        setShouldBeLoaded(true)
      } else {
        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            setShouldBeLoaded(true)
            observer.disconnect()
          }
        })
        observer.observe(node)
        return () => observer.disconnect()
      }
    }
  }, [shouldBeLoaded])

  async function playerPlay(volume: number) {
    try {
      if (videoRef.current) {
        const player = videoRef.current.getInternalPlayer()
        player.volume = volume
        if (player.play) {
          await player.play()
        } else if (player.playVideo) {
          await player.playVideo()
        } else {
          // disabled due to high amount of uninformative reports
          // rollbar.error(
          //   "Couldn't play video because the play method was not found.",
          //   { url },
          // )
        }
      }
    } catch {
      // disabled due to high amount of uninformative reports
      // rollbar.error("Couldn't play the video.", { url })
    }
  }

  async function playerPlayMuted() {
    setMuted(true)
    await playerPlay(0)
  }

  function restartVideo() {
    if (!videoRef.current) {
      return
    }
    if (videoRef.current.seekTo) {
      videoRef.current.seekTo(0, 'seconds')
      return
    }
    const player = videoRef.current.getInternalPlayer()
    if (player?.seekTo) {
      player.seekTo(0)
      return
    }
    if (typeof player?.currentTime === 'number') {
      player.currentTime = 0
    }
  }

  async function playerPlayWithSound() {
    restartVideo()
    setMuted(false)
    await playerPlay(1)
  }

  const onError = async (e: any) => {
    if (e.name === 'NotAllowedError') {
      await playerPlayMuted()
    }
  }

  // fixes autoplay issue on lecture pages
  useEffect(() => {
    if (autoplay) {
      playerPlayMuted()
    }
  }, [autoplay])

  if (!url) {
    return (
      <VideoContainerUi
        border={border}
        mobileBorder={mobileBorder}
        boxShadow={boxShadow}
        mobileBoxShadow={mobileBoxShadow}
      >
        <VideoUi id={attrId} disabledPointerEvents={false} />
      </VideoContainerUi>
    )
  }

  return (
    <VideoContainerUi
      ref={containerRef}
      border={border}
      mobileBorder={mobileBorder}
      boxShadow={boxShadow}
      mobileBoxShadow={mobileBoxShadow}
    >
      <VideoUi id={attrId} disabledPointerEvents={false}>
        {muted && <UnMute onClick={playerPlayWithSound} />}
        {shouldBeLoaded && (
          <ReactPlayer
            ref={videoRef}
            width="100%"
            height="100%"
            url={url}
            className="react-player"
            playing={playing}
            onError={onError}
            onReady={onReady}
            onPlay={() => setPlaying?.(true)}
            onStart={() => {
              const player = videoRef.current?.getInternalPlayer()
              if (player) {
                player.volume = 1 // need for YouTube, Vimeo plays with sound
              }
            }}
            muted={muted}
            onVolumeChange={() => setMuted(false)}
            controls={controls}
            playsinline={true}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  poster: posterUrl,
                },
              },
              youtube: {
                // Called when state changes to unstarted (usually when video fails to autoplay)
                onUnstarted: playerPlayMuted,
              },
            }}
          />
        )}
      </VideoUi>
    </VideoContainerUi>
  )
}

export default BasePlayer
